import type {FC} from 'react'
import {Box, Stack, Typography} from '@mui/material'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import type {CompletedWrite} from '@interfaces/alpinist/message'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'

const describeWrite = (write: CompletedWrite): string =>
  write.object_name || [write.method, write.path].filter(Boolean).join(' ') || write.tool

/**
 * The head of an answer the person stopped (AB#6760). It says the answer is
 * cut short, so the text below does not read as all of it, and what the turn
 * had already changed: Stop lets a write in flight finish and undoes none.
 */
const StoppedNotice: FC<{
  writes?: CompletedWrite[] | null
  /** `soft`: Alpinist wrapped up, so the text is an interim answer, not a fragment. */
  mode?: 'soft' | 'hard' | null
}> = ({writes, mode}) => {
  const [t] = useCustomTranslation('alpinist')

  // No list at all when the backend did not say: "no changes" would be a claim.
  let changes = null
  if (writes?.length) {
    changes = (
      <Box sx={{mt: 0.5}}>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('run_stopped_changes')}
        </Typography>
        <Box component="ul" sx={{m: 0, pl: 2.5}}>
          {writes.map((write, index) => (
            <Typography
              key={`${index}-${describeWrite(write)}`}
              component="li"
              variant="caption"
              color="text.secondary"
            >
              {describeWrite(write)}
            </Typography>
          ))}
        </Box>
      </Box>
    )
  } else if (writes) {
    changes = (
      <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.25}}>
        {t('run_stopped_no_changes')}
      </Typography>
    )
  }

  return (
    <Box sx={{mb: 0.75}}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <StopCircleOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}} />
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t(mode === 'soft' ? 'run_stopped_label_soft' : 'run_stopped_label')}
        </Typography>
      </Stack>
      {changes}
    </Box>
  )
}

export default StoppedNotice
